/**
 * Central pause/resume for Collect&Go sync — used by order UI, basket worker,
 * and the Cogo-tab HUD (via extension → Menu bridge).
 */

import {
  fetchCogoSyncSnapshot,
  pauseCogoSync,
  resumeCogoSync,
  runCogoSyncWorker,
} from './cogoSyncWorker'
import { hideSyncHud, pushSyncSnapshot } from './cogoSyncHud'
import { beginCogoAutomation, endCogoAutomation } from './cogoTabLifecycle'
import {
  getSyncRunner,
  registerSyncRunner,
  unregisterSyncRunner,
} from './cogoSyncRunners'

export async function pauseSyncFromHud(orderId: number): Promise<void> {
  getSyncRunner(orderId)?.abort.abort()
  await pauseCogoSync(orderId)
  const snap = await fetchCogoSyncSnapshot(orderId)
  if (snap) await pushSyncSnapshot(snap)
}

export async function resumeSyncFromHud(orderId: number): Promise<void> {
  if (getSyncRunner(orderId)) return
  await runSyncWorker(orderId, { continuous: true })
}

export async function stepSyncFromHud(orderId: number): Promise<void> {
  if (getSyncRunner(orderId)) return
  await runSyncWorker(orderId, { continuous: false, maxSteps: 1 })
}

async function runSyncWorker(
  orderId: number,
  opts: { continuous: boolean; maxSteps?: number }
): Promise<void> {
  const workerId = crypto.randomUUID()
  const abort = new AbortController()
  registerSyncRunner(orderId, workerId, abort)
  beginCogoAutomation(`sync:${orderId}`)

  try {
    const snap = await fetchCogoSyncSnapshot(orderId)
    if (!snap || snap.status === 'done' || snap.status === 'error') return

    if (snap.status !== 'running') {
      const job = await resumeCogoSync(orderId, workerId)
      await pushSyncSnapshot(job)
    }

    await runCogoSyncWorker({
      orderId,
      workerId,
      signal: abort.signal,
      maxSteps: opts.continuous ? undefined : opts.maxSteps,
      onSnapshot: (s) => {
        void pushSyncSnapshot(s)
      },
    })
  } catch (e) {
    console.warn('[cogoSyncControl] sync worker failed', e)
  } finally {
    unregisterSyncRunner(orderId, workerId)
    endCogoAutomation(`sync:${orderId}`)
    const snap = await fetchCogoSyncSnapshot(orderId)
    if (
      snap?.status === 'paused' ||
      snap?.status === 'waiting_choice' ||
      snap?.status === 'waiting_login'
    ) {
      if (snap) await pushSyncSnapshot(snap)
    } else {
      // done/error: worker already showed end summary then hid.
      await hideSyncHud()
    }
  }
}

const CHANNEL = 'menu-cogo-extension'

/** Listen for pause/resume clicks on the Collect&Go HUD overlay. */
export function startCogoSyncHudBridge(): () => void {
  if (typeof window === 'undefined') return () => {}

  async function onMessage(event: MessageEvent) {
    if (event.source !== window) return
    const data = event.data as {
      channel?: string
      type?: string
      action?: string
      orderId?: number
    } | null
    if (!data || data.channel !== CHANNEL || data.type !== 'syncHudAction') {
      return
    }
    const orderId = Number(data.orderId)
    if (!Number.isFinite(orderId)) return

    if (data.action === 'pause') {
      await pauseSyncFromHud(orderId)
      return
    }
    if (data.action === 'resume') {
      void resumeSyncFromHud(orderId)
      return
    }
    if (data.action === 'step') {
      void stepSyncFromHud(orderId)
    }
  }

  window.addEventListener('message', onMessage)
  return () => window.removeEventListener('message', onMessage)
}

export { registerSyncRunner, unregisterSyncRunner } from './cogoSyncRunners'
